import { CustomerSearchType } from "@app/models/CustomerSearch";
import { MenuOption } from "@app/models/MenuOption";

export const roleIds = {
    ROLE_ADMIN: 'ADMIN',
    ROLE_CORPORATE_ADMIN: 'CORPORATE_ADMIN',
    ROLE_CORPORATE_SUPERVISOR: 'CORPORATE_SUPERVISOR',
    ROLE_CORPORATE_AGENT: 'CORPORATE_AGENT',
    ROLE_THIRDPARTY_SUPERVISOR: 'THIRDPARTY_SUPERVISOR',
    ROLE_THIRDPARTY_AGENT: 'THIRDPARTY_AGENT',
    ROLE_CORPORATE_LEGAL: 'CORPORATE_LEGAL',
    ROLE_SHOPSTAR_ADMIN: 'SHOPSTAR_ADMIN',
    ROLE_SHOPSTAR_SUPERVISOR: 'SHOPSTAR_SUPERVISOR',
    ROLE_SHOPSTAR_AGENT: 'SHOPSTAR_AGENT',
}

export const PERMISSION_DESCRIPTIONS: Record<string, string> = {
    'accounts.hide': 'Permite ocultar información del usuario en determinadas vistas o listados.',
    'reports.access': 'Otorga acceso al módulo de reportes para la generación y visualización de datos.',
    'teams.access': 'Otorga acceso al módulo de equipos para su gestión y configuración.',
    'tickets.assigned_me': 'Permite visualizar los tickets asignados al usuario como resolutor.',
    'tickets.create': 'Permite crear nuevos tickets dentro del sistema.',
    'tickets.manage': 'Permite visualizar y gestionar los detalles y movimientos de un ticket.',
    'tickets.monitor': 'Permite visualizar los tickets en los que el usuario actúa como monitor.',
    'tickets.resolutor_filter': 'Permite filtrar tickets por resolutor asignado.',
    'tickets.search': 'Permite realizar búsquedas de tickets por número, cliente o referencia.',
    'tickets.store_filter': 'Permite filtrar los tickets por tienda o punto de venta.',
    'tickets.team_filter': 'Permite filtrar tickets según el equipo asignado.',
    'assignment.access': 'Otorga acceso para configurar responsables y reglas de asignación.',
    'categories.manager': 'Permite administrar las categorías del sistema (crear, editar y eliminar).',
    'dashboard.access': 'Otorga acceso al módulo de dashboard para visualizar métricas e indicadores.',
    'tickets.supervisor_filter': 'Permite filtrar tickets por supervisor asignado.',
    'assignment.as_monitor': 'Permite que el usuario pueda ser designado como monitor de un ticket.',
    'assignment.as_resolutor': 'Permite que el usuario pueda ser designado como resolutor de un ticket.',
    'teams.visible.all': 'Permite ver todos los equipos.',
    'teams.visible.org': 'Permite ver todos los equipos de las organizaciones permitidas.',
    'teams.visible.assigned': 'Permite ver solo los equipos donde el usuario está asignado.'
};

export const accountOrigins : Map<string, string> = new Map([
    ['microsoft.com', 'Usuario corporativo'],
    ['password', 'Usuario externo a mi compañía']
]);

export const ticketPriorities : Map<string, string> = new Map([
    ['default','Normal'],
    ['low', 'Medio'],
    ['high', 'Alta'],
    ['critical', 'Muy Alta']
]);

export const ticketSla : Map<string, string> = new Map([
    ['on_time', 'A tiempo'],
    ['regular', 'Por vencer'],
    ['out_of_time',' Vencido']
])

export const ticketStatusForStyle : Map<string, string> = new Map([
    ['Pendiente', '1'],
    ['En revisión', '2'],
    ['Réplica del cliente', '3'],
    ['Procesado', '4'],
    ['Sin respuesta del cliente', '5'],
    ['Finalizada', '11'],
    ['Pendiente', '9'],
    ['En revisión', '10'],
    ['Anulado', '12'],
    ['Pendiente', '14'],
    ['En revisión', '15'],
    ['Respondido', '16'],
    ['En audiencia', '17'],
    ['Re - audiencia', '18'],
    ['Finalizado', '19']
]);

export const noTicketStatusInFilters : number[] = [
    6, // Finalizada
    7, // Anulada,
    12,
    11
];

export const ticketStatusExcludedInMyTicketAssignedMe : number[] = [
    6, //Finalizada
    12,
    11
]

export const nroRecontactsInFilters : Map<string, string> = new Map([
    ['1', '1 recontacto'],
    ['2', '2 recontactos'],
    ['3', '3 recontacto'],
    ['4', '4 recontacto'],
    ['5', '5 recontacto'],
    ['6-10', 'De 6 a 10 recontactos'],
    ['11-15', 'De 11 a 15 recontactos'],
    ['16+', 'De 16 a más recontactos'],
]);

export const dateTypeInFilters : Map<string, string> = new Map([
    ['assignmentDate', 'Fecha de asignación'],
    ['creationDate', 'Fecha de creación'],
]);

export const organizationsForColor : Map<string, string> = new Map([
    ['AGORA', '#02B1FF'],
    ['AGORA_CLUB', '#F2007C'],
    ['AGORA_PAY', '#00CAFF'],
    ['AGORA_SHOP', '#FF6000'],
    ['AGORA_AHORRA', '#001FFE'],
    ['MAKRO', '#FFE500'],
    ['TPSA', '#FF4949'],
    ['PVEA', '#FF4949'],
    ['HPSA', '#F59237'],
    ['RPLAZA', '#7B61FF'],
    ['SHPSTAR', '#0970CC'],
    ['VIV', '#168D7F'],
    ['JOKR', '#5FF0AF'],
    ['MERKAO', '#3534BB'],
]);

export const optionsNewTicket: Map<string, string> = new Map([
    ['REGULAR', 'Asociado a cliente/pedido'],
    ['INTERN', 'Incidencias Internas'],
]);

export const codeGraphTitle : Map<string, string> = new Map([
    ['STORE_SUMMARY', 'Tiendas con más tickets abiertos'],
    ['RESOLUTOR_SUMMARY', 'Resolutores con más tickets abiertos'],
    ['ELAPSED_DAYS_SUMMARY', 'Días transcurridos desde la creación'],
    ['ELAPSED_DAYS', 'Días transcurridos desde la creación - Resumen'],
    ['SUBREASON_SUMMARY', 'Ranking de submotivos'],
    ['PRIORITY_SUMMARY', 'Distribución tickets abiertos según prioridad'],
    ['SLA_SUMMARY', 'Distribución tickets abiertos según SLA'],
    ['OPENED_TICKETS', 'Días transcurridos desde la creación - Resumen'],
    ['RECONTACT_SUMMARY', 'Tickets abiertos según recontactos'],
    ['SUBAREA_SUMMARY', 'Subáreas con más tickets abiertos'],
    ['AREA_SUMMARY', 'Áreas con más tickets abiertos']
]);

export const monthEnEs : Map<string, string> = new Map([
    ['enero', 'January'],
    ['febrero', 'February'],
    ['marzo', 'March'],
    ['abril', 'April'],
    ['mayo', 'May'],
    ['junio', 'June'],
    ['julio', 'July'],
    ['agosto', 'August'],
    ['setiembre', 'September'],
    ['octubre', 'October'],
    ['noviembre', 'November'],
    ['diciembre', 'December'],
])

export const monthNumEs : Map<string, string> = new Map([
    ['1', 'Enero'],
    ['2', 'Febrero'],
    ['3', 'Marzo'],
    ['4', 'Abril'],
    ['5', 'Mayo'],
    ['6', 'Junio'],
    ['7', 'Julio'],
    ['8', 'Agosto'],
    ['9', 'Setiembre'],
    ['10', 'Octubre'],
    ['11', 'Noviembre'],
    ['12', 'Diciembre'],
])

// Prefijos admitidos al inicio de un código de ticket: tenants + abreviaturas que
// usa el correlativo del backend (aria.correlative.tenant-abbreviations).
// Se ordenan de más largo a más corto para que la alternancia del regex no deje
// inalcanzable un prefijo que es extensión de otro (AGORA_SHOP vs AGORA).
export const ticketCodePrefixes: string[] = [
    'PVEA', 'VIV', 'RPLAZA', 'HPSA', 'TPSA',
    'AGORA', 'AGORA_SHOP', 'MKR', 'JOKR', 'SHPSTAR', 'MERKAO',
    'AGSP',   // abreviatura de AGORA_SHOP en tickets LDR
].sort((a, b) => b.length - a.length);

const TICKET_PREFIX_GROUP = `(${ticketCodePrefixes.join('|')})`;

export const regexTypeData : Map<string, RegExp> = new Map([
    ['email', new RegExp(/^[a-zA-Z0-9][\w\.-]*[a-zA-Z0-9]@[a-zA-Z0-9][\w\.-]*[a-zA-Z0-9]\.[a-zA-Z]{2,3}?$/)],
    ['id', new RegExp(/^\d{8,12}?$/)],//dni|pasaporte|ruc(11)-carnet de extranjeria(12)
    ['incidence', new RegExp(`^${TICKET_PREFIX_GROUP}-(?:(?:W?[A-Za-z0-9]+)-(\\d{6})-(\\d{4})|(?:INC|CD|ST)-\\d{8,10}-[0-9]+)$`)],
    ['order', new RegExp(/^((\d{13}-\d{2})|(\d{15})|(v\d+viv-\d{2})|(v\d+plzv-\d{2}|(v\d+pvqa-\d{2})|(v\d+vivqa-\d{2})))?$/)],
    ['sale-note', new RegExp(/^\d{13}?$/)],
    ['sirec', new RegExp(`^${TICKET_PREFIX_GROUP}`)],
]);

export enum CatalogType {
    TICKET_STATUS = 'TICKET_STATUS',
    USER_TEAMS = 'USER_TEAMS',
    USER_STORES = 'USER_STORES',
    USER_RESOLUTORS = 'USER_RESOLUTORS',
    ORGANIZATIONS = 'ORGANIZATIONS',
    TENANTS = "TENANTS",
    REASONS = 'REASONS',
    SUB_REASONS = 'SUB_REASONS',
    SELLERS = 'SELLERS',
    STORES = 'STORES',
    STORE_TYPES = 'STORE_TYPES',
    TEAMS = 'TEAMS',
    TEAMS_ALL = 'TEAMS_ALL',
    SLA = 'SLA',
    DATETYPE = 'DATETYPE',
    PRIORITY = 'PRIORITY',
    RECONTACTS = 'RECONTACTS',
    TAGS = 'TAGS',
    TICKET_SUBREASON_TYPE = 'TICKET_SUBREASON_TYPE',
    DIVISION_TYPES = 'DIVISION_TYPES',
    DELIVERY_MODES = 'DELIVERY_MODES',
    AREAS = 'AREAS',
    CLAIM_AREAS = 'CLAIM_AREAS',
    SUB_AREAS = 'SUB_AREAS',
    TICKET_TYPES = 'TICKET_TYPES',
    SOURCE_MEDIUM = 'SOURCE_MEDIUM'
}

export const sourceMedium: Map<string, string> = new Map([
    ['EMAIL', 'Correo electrónico'],
    ['SOCIAL_NETWORK', 'Redes sociales'],
    ['PAPER_FORM', 'LDR físico'],
    ['WEB_FORM', 'LDR web'],
    ['QR_FORM', 'LDR QR'],
    ['CUSTOMER_EMAIL', 'Correo electrónico'],
    ['CUSTOMER_ADDRESS', 'Domicilio']
]);

/**
 * Catálogo de etiquetas: alimenta tanto el filtro de tickets/dashboard como el selector de
 * etiquetado del detalle (labels-chip-dropdown).
 *
 * Las cuatro etiquetas que escribe el backend por su cuenta ("Sin Pedido", "Pedido no encontrado",
 * "Error en api pedidos", "Falta Información") deben ir con el literal EXACTO de
 * `DefaultLabel` (cc-aria-core) / `DefaultLabels` (aria-api-tickets): el filtro compara texto plano
 * y distingue mayúsculas tanto en PostgreSQL (`labels @> ARRAY[...]`) como en BigQuery
 * (`IN UNNEST(labels)`). Un solo carácter distinto deja la etiqueta sin filtrar en silencio — que
 * es lo que pasaba con 'Sin pedido' frente al 'Sin Pedido' almacenado.
 */
export const ticketLabels: string[] = [
    'Redes sociales',
    'Sin respuesta de cliente',
    'No procede (Art. 5)',
    'Anulación',
    'Falta Información',
    'Calidad',
    'Indecopi',
    'Réplica de cliente',
    'Sin Pedido',
    'Pedido no encontrado',
    'Error en api pedidos',
    'Refiere Consumo',
    'Non food',
    'Food'
];

export const searchTypeMap: Map<string, CustomerSearchType> = new Map([
  ['email', CustomerSearchType.EMAIL],
  ['id', CustomerSearchType.IDENTITY_DOCUMENT],
  ['incidence', CustomerSearchType.TICKET_NUMBER],
  ['order', CustomerSearchType.ORDER_ID],
  ['sale-note', CustomerSearchType.SALE_NOTE_NUMBER],
]);

export interface RecordInfo {
  label: string;
  displayField: string;
}
export const assignmentFormLabelMap: Map<string, string> = new Map([
  ['dispatchTenantId', 'Despachado por'],
  ['sellTenantId', 'Vendido por'],
  ['ticketReasonId', 'Motivo'],
  ['ticketSubReasonId', 'Submotivo'],
  ['ticketSubReasonType', 'Tipo de submotivo'],
  ['ticketStoreType', 'Tienda'],
  ['originType', 'Tipo de origen'],
  ['originCode', 'Origen'],
  ['deliveryMode', 'Método de entrega'],
  ['withdrawStoreCode', 'Tienda de retiro'],
  ['divisionType', 'Tipo de división'],
  ['registerOrganizationId', 'Empresa de registro'],
  ['registerStoreCode', 'Tienda de registro'],
  ['registerAreaId', 'Área de registro'],
  ['registerSubAreaId', 'Subárea de registro'],
]);

export const assignmentRecordMap: Map<string, RecordInfo> = new Map([
  ['dispatchTenantId', {label: 'Despachado por',  displayField: 'dispatchTenantName'}],
  ['sellTenantId', {label: 'Vendido por',  displayField: 'sellTenantName'}],
  ['ticketReasonId', {label: 'Motivo',  displayField: 'ticketReasonName'}],
  ['ticketSubReasonId', {label: 'Submotivo',  displayField: 'ticketSubReasonName'}],
  ['ticketSubReasonType', {label: 'Tipo de submotivo',  displayField: 'ticketSubReasonTypeDescription'}],
  ['ticketStoreType', { label: 'Tienda',  displayField: 'ticketStoreTypeName'}],
  ['originType', {label: 'Tipo de origen',  displayField: 'originTypeName'}],
  ['originCode', {label: 'Origen',  displayField: 'originName'}],
  ['deliveryMode', {label: 'Método de entrega',  displayField: 'deliveryModeName'}],
  ['withdrawStoreCode', {label: 'Tienda de retiro',  displayField: 'withdrawStoreName'}],
  ['divisionType', {label: 'Tipo de división',  displayField: 'divisionType'}],
  ['registerOrganizationId', {label: 'Empresa de registro',  displayField: 'registerOrganizationName'}],
  ['registerStoreCode', {label: 'Tienda de registro',  displayField: 'registerStoreName'}],
  ['registerAreaId', {label: 'Área de registro',  displayField: 'registerAreaName'}],
  ['registerSubAreaId', {label: 'Subárea de registro',  displayField: 'registerSubAreaName'}],
]);

export const ticketTypeMenuOptions: MenuOption[] = [
    {
        label: "Incidencia",
        value: "INCIDENCE",
        icon: {
            name: "INCIDENCE",
            cssClass: ["ticket-icon"],
            isSVG: true,
        },
    },
    {
        label: "Libro de reclamaciones",
        value: "LDR",
        icon: {
            name: "LDR",
            cssClass: ["ticket-icon"],
            isSVG: true,
        },
    },
    {
        label: "Indecopi",
        value: "INDECOPI",
        icon: {
            name: "INDECOPI",
            cssClass: ["ticket-icon"],
            isSVG: true,
        },
    },
];

export const reportOptions: any[] = [
{
    id: 'TICKET_STATUS',
    title: 'Estado de incidencia',
    desc: 'Conoce el estado <strong>actual</strong> de las incidencias.'
  },
  {
    id: 'TEAM_ATTENTION',
    title: 'Atención por equipo',
    desc: 'Conoce <strong>los resultados de la gestión</strong> de determinado equipo.'
  },
  {
    id: 'TICKET_STATUS_LDR',
    title: 'Estado de LDR',
    desc: 'Conoce el estado <strong>actual</strong> de los <strong>libros de reclamaciones.</strong>'
  },
  {
    id: 'SIREC_REPORT',
    title: 'Reportes SIREC',
    desc: 'Descarga el reporte TXT <strong>para subir LDRs masivamente en SIREC.</strong>'
  },
  {
    id: 'INDECOPI_REPORT',
    title: 'Estado de ticket de Indecopi',
    desc: 'Conoce el estado <strong>actual</strong> de los tickets de <strong>Indecopi</strong>.'
  }
]

const tenantNames = new Map([
    ['HPSA', 'Promart'],
    ['TPSA', 'Oechsle'],
    ['PVEA', 'plazaVea'],
    ['AGORA', 'Agora.pe'],
    ['RPLAZA', 'Real Plaza'],
    ['AGORA_SHOP', 'Agora Shop'],
    ['SHPSTAR', 'Shopstar'],
    ['MKR', 'Makro'],
    ['JOKR', 'Jokr'],
    ['MERKAO', 'Merkao'],
])

const sellerLDRNames = new Map([
    ['Promart', 'HPSA'],
    ['Oechsle', 'TPSA'],
    ['plazaVea', 'PVEA'],
])

const sellerNames = new Map([
    ['Promart', 'HPSA'],
    ['Oechsle', 'TPSA'],
    ['plazaVea', 'PVEA'],
    ['Vivanda', 'VIV'],
    ['Makro', 'MKR'],
    ['Jokr', 'JOKR'],
    ['Merkao', 'MERKAO'],
])

const subReasonTypeName = new Map([
    ['ORDER', 'Pedido'],
    ['CLAIM', 'Reclamo'],
    ['CONSULTATION', 'Consulta'],
    ['INTERNAL_FINDING', 'Hallazgo interno'],
    ['NONE', 'Ninguno']
])

const issueType = new Map([
    ['SERVICE', 'Servicio'],
    ['PRODUCT', 'Producto'],
])

const requestType = new Map([
    ['CLAIM', 'Reclamo'],
    ['COMPLAINT', 'Queja'],
])

export const productCode: String = 'ARIA';

export const commons = {
    adminRoles : [roleIds.ROLE_ADMIN, roleIds.ROLE_CORPORATE_ADMIN, roleIds.ROLE_SHOPSTAR_ADMIN],
    legalRoles : [roleIds.ROLE_CORPORATE_LEGAL],
    supervisorRoles : [roleIds.ROLE_CORPORATE_SUPERVISOR, roleIds.ROLE_SHOPSTAR_SUPERVISOR],
    accountOrigins : accountOrigins,
    ticketPriorities : ticketPriorities,
    ticketSla : ticketSla,
    ticketStatusForStyle : ticketStatusForStyle,
    codeGraphTitle: codeGraphTitle,
    monthEnEs: monthEnEs,
    monthNumEs,
    organizationsForColor: organizationsForColor,
    regexTypeData: regexTypeData,
    noTicketStatusInFilters: noTicketStatusInFilters,
    ticketStatusExcludedInMyTicketAssignedMe: ticketStatusExcludedInMyTicketAssignedMe,
    nroRecontactsInFilters: nroRecontactsInFilters,
    dateTypeInFilters: dateTypeInFilters,
    optionsNewTicket: optionsNewTicket,
    catalogType: CatalogType,
    searchTypeMap: searchTypeMap,
    subReasonTypeName,
    sourceMedium: sourceMedium,
    ticketLabels: ticketLabels,
    tenantNames,
    sellerNames,
    sellerLDRNames,
    assignmentRecordMap,
    issueType,
    requestType,
    // Motivo "reembolso": habilita los campos de reembolso para el tenant AGORA_SHOP.
    refundReasonId: 15
}

export const skipAuthErrorPaths: string[] = [
  '/notifications'
];
